import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '../auth/authStore.js';
import { supabase } from '../lib/supabase.js';

/**
 * Missions & coins — client read side (MISSIONS.md Phase A).
 *
 * One RPC does everything: `refresh_missions()` recomputes progress from
 * the server's own source tables, awards coins (idempotent, unified 400/month
 * cap), and returns the full screen payload. The client never reports
 * progress — coins are money-valued, so the same trust rule as the
 * subscription page applies: render only what the server computed.
 */

export type MissionTier = 'daily' | 'weekly' | 'monthly';

export interface Mission {
  id: string;
  tier: MissionTier;
  coins: number;
  target: number;
  progress: number;
  completed_at: string | null;
  /** What the ledger actually paid for this period (null = nothing yet).
   *  Can be < coins when the monthly cap clipped the award. */
  awarded: number | null;
}

export interface MissionsState {
  balance: number;
  /** Earned this month; clipped at month_cap. */
  month_awarded: number;
  /** ONE unified cap: earn <= this per month AND Phase B spends <= this
   *  per monthly charge — every earned coin is usable. */
  month_cap: number;
  missions: Mission[];
}

export const MISSIONS_KEY = (userId: string) => ['missions', userId] as const;

function parseMissionsPayload(raw: unknown): MissionsState {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const missions: Mission[] = (Array.isArray(obj.missions) ? obj.missions : [])
    .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
    .filter(
      (m) =>
        typeof m.id === 'string' &&
        (m.tier === 'daily' || m.tier === 'weekly' || m.tier === 'monthly') &&
        typeof m.coins === 'number' &&
        typeof m.target === 'number' &&
        m.target > 0,
    )
    .map((m) => ({
      id: m.id as string,
      tier: m.tier as MissionTier,
      coins: m.coins as number,
      target: m.target as number,
      progress: typeof m.progress === 'number' ? m.progress : 0,
      completed_at: typeof m.completed_at === 'string' ? m.completed_at : null,
      awarded: typeof m.awarded === 'number' ? m.awarded : null,
    }));
  return {
    balance: typeof obj.balance === 'number' ? obj.balance : 0,
    month_awarded: typeof obj.month_awarded === 'number' ? obj.month_awarded : 0,
    month_cap: typeof obj.month_cap === 'number' ? obj.month_cap : 400,
    missions,
  };
}

/** Fetch = refresh: every read recomputes on the server (idempotent). */
export function useMissions(): UseQueryResult<MissionsState> {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? MISSIONS_KEY(user.id) : ['missions', 'anon'],
    enabled: !!user,
    // The RPC recomputes + writes on every call; it's idempotent but the
    // most expensive query on the hottest path (Home). Keep it calm.
    staleTime: 120 * 1000,
    retry: 1,
    queryFn: async (): Promise<MissionsState> => {
      const { data, error } = await supabase.rpc('refresh_missions');
      if (error) throw error;
      return parseMissionsPayload(data);
    },
  });
}

/* Invalidation after a lesson/practice/flashcard completion is NOT a hook:
 * it lives in `invalidateProgress()` (progress/hooks.ts) next to the other
 * post-completion cache writes, so every flusher and mutation gets it
 * without each call site remembering missions exist. Import MISSIONS_KEY
 * from here if you need a one-off invalidation. */

export const missionsByTier = (s: MissionsState, tier: MissionTier): Mission[] =>
  s.missions.filter((m) => m.tier === tier);

export const isDone = (m: Mission): boolean => m.completed_at !== null;
